export type TeacherState =
  | "IDLE"
  | "SPEAK"
  | "SPEAKING"
  | "LISTEN"
  | "LISTENING"
  | "WAIT"
  | "WAITING"
  | "ACKNOWLEDGE"
  | "PRAISE"
  | "PRAISING"
  | "RETRY"
  | "RETRYING"
  | "MODEL"
  | "MODELING"
  | "CELEBRATE"
  | "CELEBRATING";

export interface LivingAvatarOptions {
  container: HTMLElement;
  initialState?: TeacherState;
}

export interface VideoSlot {
  readonly src: string;
  readonly loop: boolean;
  readonly purpose: string;
}

export const APPROVED_STATE_VIDEOS: Readonly<Record<string, VideoSlot>> = Object.freeze({
  IDLE: { src: "/assets/avatar/ltac/idle.mp4", loop: true, purpose: "대기 상태의 자연스러운 호흡 및 눈맞춤" },
  SPEAK: { src: "/assets/avatar/ltac/speak.mp4", loop: true, purpose: "질문 및 안내 시의 다정한 설명 제스처" },
  SPEAKING: { src: "/assets/avatar/ltac/speak.mp4", loop: true, purpose: "질문 및 안내 시의 다정한 설명 제스처" },
  LISTEN: { src: "/assets/avatar/ltac/listen.mp4", loop: true, purpose: "아동 발화 대기 시 고개 기울임과 집중 경청" },
  LISTENING: { src: "/assets/avatar/ltac/listen.mp4", loop: true, purpose: "아동 발화 대기 시 고개 기울임과 집중 경청" },
  WAIT: { src: "/assets/avatar/ltac/listen.mp4", loop: true, purpose: "아동 생각 시간 배려 및 온화한 경청 유지" },
  WAITING: { src: "/assets/avatar/ltac/listen.mp4", loop: true, purpose: "아동 생각 시간 배려 및 온화한 경청 유지" },
  ACKNOWLEDGE: { src: "/assets/avatar/ltac/acknowledge.mp4", loop: false, purpose: "소리 감지 즉시 '응! 들었어' 눈웃음 및 고개 끄덕임" },
  PRAISE: { src: "/assets/avatar/ltac/praise.mp4", loop: false, purpose: "정답 및 신나는 참여에 대한 손뼉/환호" },
  PRAISING: { src: "/assets/avatar/ltac/praise.mp4", loop: false, purpose: "정답 및 신나는 참여에 대한 손뼉/환호" },
  RETRY: { src: "/assets/avatar/ltac/retry.mp4", loop: false, purpose: "오답/재시도 시 다정한 고개 끄덕임과 격려" },
  RETRYING: { src: "/assets/avatar/ltac/retry.mp4", loop: false, purpose: "오답/재시도 시 다정한 고개 끄덕임과 격려" },
  MODEL: { src: "/assets/avatar/ltac/model.mp4", loop: false, purpose: "힌트 및 시범 보여주기 제스처" },
  MODELING: { src: "/assets/avatar/ltac/model.mp4", loop: false, purpose: "힌트 및 시범 보여주기 제스처" },
  CELEBRATE: { src: "/assets/avatar/ltac/celebrate.mp4", loop: false, purpose: "세션 완료 시 환한 미소와 큰 축하" },
  CELEBRATING: { src: "/assets/avatar/ltac/celebrate.mp4", loop: false, purpose: "세션 완료 시 환한 미소와 큰 축하" },
});

export class LivingAvatar {
  readonly #container: HTMLElement;
  #state: TeacherState = "IDLE";
  #audioAnalyser: AnalyserNode | null = null;

  // DOM Elements
  #bufferA: HTMLVideoElement | null = null;
  #bufferB: HTMLVideoElement | null = null;
  #activeBuffer: HTMLVideoElement | null = null;
  #currentSlotSrc = "";
  #stateBadge: HTMLElement | null = null;

  constructor(options: LivingAvatarOptions) {
    this.#container = options.container;
    this.#buildDOM();
    this.#prefetchVideos();
    this.setState(options.initialState ?? "IDLE");
  }

  #prefetchVideos(): void {
    if (typeof document === "undefined" || !document.head) return;
    const uniqueUrls = new Set(Object.values(APPROVED_STATE_VIDEOS).map((v) => v.src));
    uniqueUrls.forEach((src) => {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.as = "video";
      link.href = src;
      document.head.appendChild(link);
    });
  }

  #buildDOM(): void {
    this.#container.innerHTML = `
      <div class="living-avatar-stage" data-state="IDLE">
        <div class="avatar-aura"></div>
        <div class="avatar-glow"></div>
        <div class="avatar-motion-wrapper">
          <!-- Dual-Buffer Video Engine for Seamless BVE State Transitions (VA-01 ~ VA-08) -->
          <video class="avatar-video-buffer active" muted playsinline aria-hidden="true"></video>
          <video class="avatar-video-buffer standby" muted playsinline aria-hidden="true"></video>

          <!-- Listening Pulse Effect -->
          <div class="avatar-listening-indicator" aria-hidden="true">
            <span class="pulse-ring ring-1"></span>
            <span class="pulse-ring ring-2"></span>
            <span class="listening-icon">👂</span>
          </div>
        </div>

        <!-- Teacher Status Badge -->
        <div class="living-teacher-status" aria-live="polite">
          <strong class="status-name">지나쌤</strong>
          <span class="status-label">따뜻하게 바라보고 있어요</span>
        </div>
      </div>
    `;

    const videos = this.#container.querySelectorAll<HTMLVideoElement>(".avatar-video-buffer");
    this.#bufferA = videos[0] ?? null;
    this.#bufferB = videos[1] ?? null;
    this.#activeBuffer = this.#bufferA;
    this.#stateBadge = this.#container.querySelector<HTMLElement>(".status-label");

    const onVideoError = (ev: Event) => {
      const vid = ev.currentTarget as HTMLVideoElement;
      console.warn(`[LivingAvatar] Video load failed: ${vid.src}`);
      if (this.#stateBadge) {
        this.#stateBadge.textContent = "영상 연결 재시도 중…";
      }
    };
    this.#bufferA?.addEventListener("error", onVideoError);
    this.#bufferB?.addEventListener("error", onVideoError);
  }

  get state(): TeacherState {
    return this.#state;
  }

  get currentSrc(): string {
    return this.#activeBuffer?.currentSrc || this.#currentSlotSrc || "";
  }

  get activeBuffer(): HTMLVideoElement | null {
    return this.#activeBuffer;
  }

  attachAudioAnalyser(analyser: AnalyserNode | null): void {
    this.#audioAnalyser = analyser;
  }

  setState(state: TeacherState, customMessage?: string): void {
    this.#state = state;
    const stage = this.#container.querySelector<HTMLElement>(".living-avatar-stage");
    if (stage) stage.dataset.state = state;

    // 1. Play matching BVE state video (VA-01 ~ VA-08)
    const slot = APPROVED_STATE_VIDEOS[state] ?? APPROVED_STATE_VIDEOS.IDLE!;
    this.#playVideoSlot(slot, state);

    // 2. Update status badge label
    if (this.#stateBadge) {
      switch (state) {
        case "SPEAK":
        case "SPEAKING":
          this.#stateBadge.textContent = customMessage ?? "다정하게 이야기하고 있어요";
          break;
        case "LISTEN":
        case "LISTENING":
          this.#stateBadge.textContent = customMessage ?? "지나쌤이 귀 기울여 듣고 있어요 👂";
          break;
        case "ACKNOWLEDGE":
          this.#stateBadge.textContent = customMessage ?? "응! 지나쌤이 들었어 😊";
          break;
        case "WAIT":
        case "WAITING":
          this.#stateBadge.textContent = customMessage ?? "아이의 생각을 천천히 기다려요";
          break;
        case "PRAISE":
        case "PRAISING":
          this.#stateBadge.textContent = customMessage ?? "우와! 정말 잘했어! ✨";
          break;
        case "RETRY":
        case "RETRYING":
          this.#stateBadge.textContent = customMessage ?? "괜찮아, 천천히 다시 해보자 🌱";
          break;
        case "MODEL":
        case "MODELING":
          this.#stateBadge.textContent = customMessage ?? "지나쌤과 함께 세어볼까? 👆";
          break;
        case "CELEBRATE":
        case "CELEBRATING":
          this.#stateBadge.textContent = customMessage ?? "오늘 정말 최고였어! 🎉";
          break;
        case "IDLE":
        default:
          this.#stateBadge.textContent = customMessage ?? "오늘도 네 편이야";
          break;
      }
    }
  }

  #playVideoSlot(slot: VideoSlot, state: TeacherState): void {
    if (!this.#bufferA || !this.#bufferB) return;
    if (this.#currentSlotSrc === slot.src && this.#activeBuffer && !this.#activeBuffer.paused) {
      return;
    }
    this.#currentSlotSrc = slot.src;

    const standby = (this.#activeBuffer === this.#bufferA) ? this.#bufferB : this.#bufferA;
    const outgoing = this.#activeBuffer;

    standby.src = slot.src;
    standby.loop = slot.loop;
    standby.currentTime = 0;
    standby.dataset.targetState = state;

    standby.classList.add("active");
    standby.classList.remove("standby");
    if (outgoing && outgoing !== standby) {
      outgoing.classList.remove("active");
      outgoing.classList.add("standby");
      setTimeout(() => {
        if (outgoing !== this.#activeBuffer) outgoing.pause?.();
      }, 200);
    }
    this.#activeBuffer = standby;

    const attemptPlay = (isRetry = false) => {
      try {
        const promise = standby.play?.();
        if (promise !== undefined && typeof promise.then === "function") {
          promise.catch((err: unknown) => {
            if (!isRetry) {
              // Q09: 실제 1회 재시도 (250ms 지연)
              setTimeout(() => {
                attemptPlay(true);
              }, 250);
            } else {
              // Q09: 재시도 실패 시 안전한 실패 상태 처리 (허위 '재시도 중' 표출 금지, 구형 렌더러 fallback 금지)
              const stage = this.#container.querySelector<HTMLElement>(".living-avatar-stage");
              if (stage) stage.dataset.videoStatus = "FAILED";
              if (this.#stateBadge) {
                this.#stateBadge.textContent = "화면을 터치하면 영상이 다시 재생돼요 👆";
              }
              console.warn("[LivingAvatar] video.play() failed after retry:", err);
            }
          });
        }
      } catch (err) {
        if (!isRetry) {
          setTimeout(() => attemptPlay(true), 250);
        } else {
          const stage = this.#container.querySelector<HTMLElement>(".living-avatar-stage");
          if (stage) stage.dataset.videoStatus = "FAILED";
        }
      }
    };

    attemptPlay(false);
  }

  dispose(): void {
    this.#bufferA?.pause?.();
    this.#bufferB?.pause?.();
    this.#activeBuffer = null;
    this.#audioAnalyser = null;
  }
}
